import { motion, AnimatePresence } from "framer-motion";
import { Brain, X, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import FeedbackButtons from "@/components/FeedbackButtons";
import { renderBulletContent } from "./markdown";

const AdvisorMessage = ({ msg, idx, userId }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10 }}
    className={`${msg.type === "user" ? "ml-12 bg-ism-teal/10 border-ism-teal/20" : "mr-12 bg-white/5 border-white/10"} border rounded-xl p-4`}
  >
    <div className="flex items-center justify-between mb-2">
      <div className={`text-xs font-medium ${msg.type === "user" ? "text-ism-teal" : "text-gray-400"}`}>{msg.type === "user" ? "You" : "Advisor"}</div>
      {msg.type === "advisor" && idx > 0 && (
        <FeedbackButtons userId={userId} agentType="advisor" outputSummary={msg.content?.substring(0, 200)} context={{ messageIndex: idx }} />
      )}
    </div>
    <div className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap">{renderBulletContent(msg.content)}</div>
  </motion.div>
);

export const AdvisorModal = ({ open, userId, messages, processing, question, setQuestion, onSend, onClose }) => (
  <AnimatePresence>
    {open && (
      <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
        <motion.div
          className="glass-card relative z-10 w-full max-w-2xl max-h-[85vh] flex flex-col rounded-2xl overflow-hidden border border-ism-teal/30"
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-5 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-ism-teal/10 to-transparent flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-ism-teal/20 border border-ism-teal/30">
                <Brain className="h-6 w-6 text-ism-teal" />
              </div>
              <div>
                <h2 className="text-xl text-white font-medium">Sales Strategy Advisor</h2>
                <p className="text-xs text-gray-400">Ask about strategy, deals, accounts, or market insights</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>

          <ScrollArea className="flex-1 p-5">
            <div className="space-y-4">
              <AnimatePresence>
                {messages.map((msg, idx) => <AdvisorMessage key={`${idx}-${msg.type}`} msg={msg} idx={idx} userId={userId} />)}
              </AnimatePresence>
              {processing && (
                <motion.div className="flex items-center gap-2 text-gray-500 p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <Loader2 className="h-4 w-4 animate-spin text-ism-teal" />
                  <span className="text-sm">Advisor is thinking...</span>
                </motion.div>
              )}
            </div>
          </ScrollArea>

          <div className="p-4 border-t border-white/10 bg-white/5 flex gap-2 flex-shrink-0">
            <Input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && onSend()}
              placeholder="Ask about sales strategy, deals, accounts..."
              className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 rounded-lg focus:border-ism-teal"
              disabled={processing}
              data-testid="advisor-input"
            />
            <Button onClick={onSend} disabled={processing || !question.trim()} className="bg-ism-teal hover:bg-ism-teal/90 text-black rounded-lg px-4" data-testid="advisor-send-btn">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);
